/* How to get a book's worth of history into it.
 *
 * Everything else in this app is about a book that already has something in it.
 * This is the part nobody writes down: the app knows what it holds because
 * somebody told it, and a new book is empty in four separate ways at once —
 * no positions, no deposits, no past values, no trades. Each has its own way
 * in, and none of them is guessable from the screen it lives on.
 *
 * Written as disclosures rather than a wall, because it is read once and then
 * only ever in the one place somebody is stuck. Shut, it is a list of the four
 * questions; open, it is the exact columns.
 *
 * The last one is the honest bit. The realistic way anyone fills a book like
 * this is by handing statements and screenshots to a language model and asking
 * it for a file — so rather than pretend otherwise, the prompt that gets a
 * usable answer is here, ready to copy, with the rules that keep the answer
 * from being made up. */

import { useRef, useState } from 'react';

/* The prompt, kept as one string so what is copied is exactly what is shown.
 *
 * Most of it is not about formats. It is the three ways an obliging assistant
 * gets this wrong: filling gaps with plausible numbers, counting a transfer
 * between your own accounts as new money, and converting a two-year-old deposit
 * at today's rate. Each is stated as a refusal rather than a preference. */
export const AI_PROMPT = `I am filling in a private portfolio tracker and I need you to turn my records into CSV files it can import. I will give you statements, exports or screenshots.

Rules that matter more than the format:
- Never invent a number. If a figure is not in what I gave you, leave the cell empty and tell me which rows are incomplete. Do not estimate, round to something tidy, or fill a gap with a plausible value.
- A transfer between my own accounts is not a deposit. Moving money from a złoty subaccount into a dollar one at the same broker adds nothing. Mark those rows FX_SWAP so they are recorded and not counted as money paid in.
- Use the exchange rate from each row's own date, never today's. If you cannot look one up, leave rate_to_usd empty rather than guessing.
- Tell me what you could not read, and what you had to decide.

Produce whichever of these I ask for, each in its own \`\`\`csv block.

1. Deposits and withdrawals
date,time,account,type,currency,amount,rate_to_usd,note
- date is YYYY-MM-DD. time is HH:MM and optional.
- type is DEPOSIT, WITHDRAWAL or FX_SWAP.
- amount is always positive; the type says the direction.
- account is my own name for the broker, consistently spelled.
- rate_to_usd is the USD value of one unit of that currency on that date. Put 1 for USD rows. Leaving it empty is fine and the app will look it up.

2. What the portfolio was worth in the past
date,value_usd
- One row per trading day. Monthly rows draw as separate dots rather than a line, so give me every weekday if you have enough to interpolate sensibly, and say so if you interpolated.

3. Trades
date,ticker,side,shares,price
- side is buy or sell. price is per share, in the currency the share trades in.

Ask me for anything you need before producing a file. I would rather answer a question than correct a guess.`;

function Topic({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderTop: '1px solid var(--edge)' }}>
      <button
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
          padding: '13px 0', textAlign: 'left', fontSize: 13.5, fontWeight: 600,
        }}
      >
        <span>{title}</span>
        <span className="dim" aria-hidden="true" style={{ marginLeft: 'auto', fontSize: 15 }}>
          {open ? '−' : '+'}
        </span>
      </button>
      {open ? <div style={{ paddingBottom: 14 }}>{children}</div> : null}
    </div>
  );
}

function Cols({ children }: { children: React.ReactNode }) {
  return (
    <div className="mono" style={{
      fontSize: 11, background: 'var(--card)', border: '1px solid var(--edge)',
      borderRadius: 'var(--r-sm)', padding: '9px 11px', margin: '8px 0',
      overflowX: 'auto', whiteSpace: 'pre', lineHeight: 1.6,
    }}>{children}</div>
  );
}

export function DataGuide({ say }: { say: (m: string) => void }) {
  const promptRef = useRef<HTMLDivElement | null>(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(AI_PROMPT);
      say('Prompt copied. Paste it into whichever assistant you use, along with your statements.');
      return;
    } catch {
      /* Writing to the clipboard needs a permission some shells refuse, and a
         button that silently does nothing is worse than one that does something
         else. Selecting the text turns the failure into one long press. */
    }
    const node = promptRef.current;
    if (node) {
      const range = document.createRange();
      range.selectNodeContents(node);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      node.scrollIntoView({ block: 'center' });
      say('This device would not let the app reach the clipboard, so the prompt is selected. Hold and copy it.');
    } else {
      say('This device would not let the app reach the clipboard. Select the prompt and copy it by hand.');
    }
  };

  return (
    <section>
      <div className="section-head">
        <h2>Filling a new book</h2>
        <span className="meta">four ways in</span>
      </div>
      <div className="card card-pad">
        <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.5, marginBottom: 4 }}>
          Nothing here is worked out from anything else. The book knows what it holds because you
          told it, and there are four separate things to tell it. None has to be done at once, and
          the book is useful after the first.
        </div>

        <Topic title="1 · What you hold">
          <div className="dim" style={{ fontSize: 12, lineHeight: 1.55 }}>
            <strong>Add a position</strong>, on the value screen, one company at a time: how many
            shares, what they cost on average, and which account they sit in. That is enough for
            prices, weights and limits to start working.
            <br /><br />
            A cost you do not know is left blank rather than guessed, because the book has a word for
            "unknown" and reports the position as unpriced instead of quietly valuing it wrong.
          </div>
        </Topic>

        <Topic title="2 · Money you paid in">
          <div className="dim" style={{ fontSize: 12, lineHeight: 1.55 }}>
            <strong>Money in and out → Bring a history in.</strong> Without this the book cannot
            tell what the market added from what you funded, so the paid-in line and the return
            figure stay empty.
            <Cols>{`date,time,account,type,currency,amount,rate_to_usd,note
2025-11-17,22:35,REVOLUT,DEPOSIT,USD,6109.42,1,
2026-04-18,02:42,REVOLUT,WITHDRAWAL,USD,2022.00,1,`}</Cols>
            Only <span className="mono">date</span>, <span className="mono">type</span>,{' '}
            <span className="mono">currency</span> and <span className="mono">amount</span> are
            needed; columns are read by their headings in any order.{' '}
            <span className="mono">FX_SWAP</span> marks a transfer between your own accounts, so it is
            listed and deliberately not counted as money paid in.
            <br /><br />
            <span className="mono">rate_to_usd</span> is optional: leave it out and the app fetches
            the rate for each date, which takes a few seconds. Fill it in and the import is instant.
          </div>
        </Topic>

        <Topic title="3 · What it was worth before">
          <div className="dim" style={{ fontSize: 12, lineHeight: 1.55 }}>
            <strong>Money in and out → Value timeline → Bring a past in.</strong> The book records
            its own value from the day you install it. Anything earlier has to be typed in, and no
            broker will give it back to you.
            <Cols>{`date,value_usd
2025-08-31,14941.00
2025-09-30,19891.00`}</Cols>
            These arrive marked as typed in and the chart keeps them apart from what it measured.
            Nothing can land on or after the first day the book measured itself, because that part actually
            happened and is not replaced by an estimate.
            <br /><br />
            One row a month draws as separate points; a row per trading day draws as a line.
          </div>
        </Topic>

        <Topic title="4 · What you bought and sold">
          <div className="dim" style={{ fontSize: 12, lineHeight: 1.55 }}>
            Three ways, in order of how little work they are:
            <br /><br />
            <strong>A broker's own file.</strong> Brokers → the account → Import trades from a
            file. CSV or TSV straight from the export; you match the columns once and it remembers.
            <br /><br />
            <strong>A read-only key.</strong> Brokers → Broker connection. Interactive Brokers hands
            over a statement; MEXC hands over the futures balance and open contracts, and keeps
            reading them on its own. Both work only in the installed apps.
            <br /><br />
            <strong>By hand.</strong> Taxes → the trade log, for the ones that never made it into
            an export.
            <br /><br />
            Until trades exist, a company's chart shows your average cost as a level and no buy or
            sell marks, because it has no fills to put on it.
          </div>
        </Topic>

        <Topic title="Getting an assistant to prepare the files">
          <div className="dim" style={{ fontSize: 12, lineHeight: 1.55 }}>
            The realistic way to turn years of statements into these files is to hand them to a
            language model. It is also the fastest way to end up with confident, invented numbers in
            a portfolio tracker, so the prompt below spends most of its length forbidding exactly
            that: no filled gaps, no transfer counted as a deposit, no historical amount converted
            at today's rate.
            <br /><br />
            Copy it, paste it into whichever assistant you use, and attach your exports or
            screenshots. Then check the totals it gives you against your broker's own before you
            import anything.
          </div>
          <div ref={promptRef} className="mono" style={{
            fontSize: 10.5, background: 'var(--card)', border: '1px solid var(--edge)',
            borderRadius: 'var(--r-sm)', padding: '10px 12px', marginTop: 10,
            maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.5,
          }}>{AI_PROMPT}</div>
          <button className="btn btn-block btn-primary" style={{ marginTop: 10 }}
                  onClick={() => void copy()}>
            Copy the prompt
          </button>
        </Topic>
      </div>
    </section>
  );
}
